import React from "react";
import PropTypes from "prop-types";
import { InputText, Label, Dropdown } from "../../../../components/Inputs";
import {
  COMPANY_TYPE_LABELS,
  validateAreaCode,
} from "../../../../components/Utils";

const getState = (model) => {
  const address = model.address || {};
  return {
    name: model.name || "",
    typeId: model.type_id || "",
    street: address.street || "",
    houseNumber: address.houseNumber || "",
    areaCode: address.areaCode || "",
    city: address.city || "",
    footPrint: model.footPrint || "",
    contractNumber: model.contractNumber || "",
    error: {
      name: null,
      typeId: null,
      street: null,
      houseNumber: null,
      areaCode: null,
      city: null,
    },
  };
};

function BasicDataForm({
  forwardedRef,
  className = "",
  model = {
    name: "",
    type_id: "",
    address: {
      street: "",
      houseNumber: "",
      areaCode: "",
      city: "",
    },
    contractGiver: "",
    footPrint: "",
    contractNumber: "",
  },
  isVertical,
  namePrefix = "",
  disabled: _disabled,
  onCompanyTypeChanged,
  types,
  onlyServicePartner,
  readOnly,
}) {
  const [state, setState] = React.useState(getState(model));
  const disabled = readOnly || _disabled;
  const handleChange = React.useCallback((key, value) => {
    setState((prev) => ({
      ...prev,
      [key]: value,
      error: {
        ...prev.error,
        [key]: value === "",
      },
    }));
  }, []);

  const validate = React.useCallback(
    (noValidate) => {
      const { error, ...newModel } = state;
      // check if all values are set
      let isValid = true;
      Object.keys(newModel).forEach((key) => {
        if (
          !noValidate &&
          error[key] !== undefined &&
          (newModel[key] === "" || newModel[key] === null)
        ) {
          error[key] = true;
          isValid = false;
        }
      });
      if (!validateAreaCode(newModel.areaCode)) {
        isValid = false;
        error.areaCode = true;
      }
      if (!noValidate && !isValid) {
        setState((prev) => ({ ...prev, error }));
        return null;
      }
      const { name, typeId, footPrint, contractNumber, ...address } = newModel;
      return {
        id: model.id,
        name,
        type_id: typeId,
        footPrint,
        contractNumber,
        address: { ...model.address, ...address },
        contractGiver: model.contractGiver,
      };
    },
    [model, state],
  );

  const getModel = React.useCallback(
    (noValidate) => validate(noValidate),
    [validate],
  );
  React.useImperativeHandle(forwardedRef, () => ({ getModel }));

  const handleCompanyTypeChange = React.useCallback(
    (value) => {
      setState((prev) => ({ ...prev, typeId: value }));
      onCompanyTypeChanged && onCompanyTypeChanged(value);
    },
    [onCompanyTypeChanged],
  );

  const renderStreetHouse = React.useCallback(
    (className) => {
      return (
        <div
          className={`${className} ${
            state.error.street || state.error.houseNumber ? " error-group-" : ""
          }`}
        >
          <Label
            className="display-inline-block"
            htmlFor={`${namePrefix}address_form_street`}
          >
            {`Straße`}
          </Label>
          <Label className="display-inline-block">{`/`}</Label>
          <Label
            className="display-inline-block"
            htmlFor={`${namePrefix}address_form_housenumber`}
          >
            {`Hausnummer*`}
          </Label>
          <div className="w3-row">
            <div className="w3-col s8 pad-sm-rht">
              <InputText
                id={`${namePrefix}address_form_street`}
                data-test-id={`${namePrefix}address_form_street`}
                className={`w3-block w3-border ${
                  state.error.street ? "w3-border-red" : ""
                }`}
                value={state.street}
                onChange={(event) => handleChange("street", event.target.value)}
                disabled={disabled}
              />
            </div>
            <div className="w3-col s4 pad-sm-lft">
              <InputText
                id={`${namePrefix}address_form_housenumber`}
                data-test-id={`${namePrefix}address_form_housenumber`}
                className={`w3-block w3-border ${
                  state.error.houseNumber ? "w3-border-red" : ""
                }`}
                value={state.houseNumber}
                onChange={(event) =>
                  handleChange("houseNumber", event.target.value)
                }
                disabled={disabled}
              />
            </div>
          </div>
        </div>
      );
    },
    [state, namePrefix, disabled],
  );

  const renderAreaCity = React.useCallback(
    (className) => {
      return (
        <div
          className={`${className} ${
            state.error.areaCode || state.error.city ? " error-group-" : ""
          }`}
        >
          <Label
            className="display-inline-block"
            htmlFor={`${namePrefix}area_code`}
          >
            {`PLZ`}
          </Label>
          <Label className="display-inline-block">{`/`}</Label>
          <Label className="display-inline-block" htmlFor={`${namePrefix}city`}>
            {`Ort*`}
          </Label>
          <div className="w3-row">
            <div className="w3-col s4 pad-sm-rht">
              <InputText
                id={`${namePrefix}area_code`}
                data-test-id={`${namePrefix}address_form_area_code`}
                className={`w3-block w3-border ${
                  state.error.areaCode ? "w3-border-red" : ""
                }`}
                value={state.areaCode}
                onChange={(event) =>
                  handleChange("areaCode", event.target.value)
                }
                disabled={disabled}
              />
            </div>
            <div className="w3-col s8 pad-sm-lft">
              <InputText
                id={`${namePrefix}city`}
                data-test-id={`${namePrefix}address_form_city`}
                className={`w3-block w3-border ${
                  state.error.city ? "w3-border-red" : ""
                }`}
                value={state.city}
                onChange={(event) => handleChange("city", event.target.value)}
                disabled={disabled}
              />
            </div>
          </div>
        </div>
      );
    },
    [state, namePrefix, disabled],
  );

  const renderOptionalFields = React.useCallback(() => {
    return (
      <div className="w3-row neg-margin-lr">
        <div className="w3-col s6 pad-lr pad-top form-group-item">
          <Label
            htmlFor={`${namePrefix}contractNumber`}
          >{`Rahmenvertragsnummer`}</Label>
          <InputText
            id={`${namePrefix}contractNumber`}
            data-test-id={`${namePrefix}address_form_contract_number`}
            className={`w3-block w3-border`}
            value={state.contractNumber}
            onChange={(event) =>
              handleChange("contractNumber", event.target.value)
            }
            disabled={disabled}
          />
        </div>
      </div>
    );
  }, [state, namePrefix, disabled]);

  const TypeOptions = React.useMemo(() => {
    let typeOptions = (types || []).map((item) => {
      return { label: item.label, value: item.id };
    });
    // exclude TypeID = 4 (Firma) when creating Service Partners (KDLs and DLs)
    if (model.type_id === 1) {
      typeOptions = [
        {
          label: COMPANY_TYPE_LABELS[`${model.type_id}`],
          value: model.type_id,
        },
      ];
    } else {
      typeOptions = typeOptions.filter((item) =>
        onlyServicePartner ? item.value < 4 : item.value >= 4,
      );
    }
    return typeOptions;
  }, [types, model, onlyServicePartner]);

  return (
    <div className={className}>
      <div className="neg-margin-lr w3-row">
        <div
          className={`w3-col s6 pad-lr pad-top form-group-item ${
            state.error.name && " error-group"
          }`}
        >
          <Label htmlFor={`${namePrefix}name`}>{`Firmenname*`}</Label>
          <InputText
            id={`${namePrefix}name`}
            data-test-id={`${namePrefix}address_form_name`}
            className={`w3-block w3-border`}
            value={state.name}
            onChange={(event) => handleChange("name", event.target.value)}
            disabled={disabled}
          />
        </div>
        <div
          className={`w3-col s6 pad-lr pad-top form-group-item ${
            state.error.typeId && " error-group"
          }`}
        >
          <Label htmlFor={`${namePrefix}typeId`}>{`Firmentyp*`}</Label>
          <Dropdown
            id={`${namePrefix}typeId`}
            data-test-id={`${namePrefix}address_form_type_id`}
            className={`w3-block w3-border`}
            value={state.typeId}
            options={TypeOptions}
            onChange={(event) => handleCompanyTypeChange(event.value)}
            disabled={disabled || model.type_id === 1}
            showClear
          />
        </div>
      </div>
      <div className="neg-margin-lr">
        <div className="w3-row">
          {renderStreetHouse(
            `w3-col ${
              isVertical ? "m12" : "m6"
            } pad-lr pad-top form-group-item`,
          )}
          {renderAreaCity(
            `w3-col ${
              isVertical ? "m12" : "m6"
            } pad-lr pad-top form-group-item`,
          )}
        </div>
      </div>
      {onlyServicePartner && renderOptionalFields()}
    </div>
  );
}

BasicDataForm.propTypes = {
  className: PropTypes.string,
  model: PropTypes.object,
  types: PropTypes.array,
  userCompany: PropTypes.object,
  isVertical: PropTypes.bool,
  namePrefix: PropTypes.string,
  onCompanyTypeChanged: PropTypes.func,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  onlyServicePartner: PropTypes.bool,
};

export default React.forwardRef((props, ref) => (
  <BasicDataForm {...props} forwardedRef={ref} />
));
